import { useMemo } from 'react'

import { type SelectValue } from '@procurify/ui'
import { type FormikHelpers } from 'formik'
import * as Yup from 'yup'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from '../../messages'
import {
  type GlobalCommandOrgCategoriesFormSchema,
  type IGlobalCommandOrgCategoriesFormPrimaryCategory,
  type IGlobalCommandOrgCategoriesFormSecondaryCategory,
  type IGlobalCommandOrgCategoriesFormValue,
} from '../../types'

export const useGlobalCommandOrgCategoriesFormFormik = ({
  initialValue,
  onSubmit,
}: {
  initialValue?: IGlobalCommandOrgCategoriesFormValue
  onSubmit: (value: IGlobalCommandOrgCategoriesFormValue) => void
}) => {
  const intl = useProcurifyIntl()

  const initialValues = useMemo<GlobalCommandOrgCategoriesFormSchema>(
    () => ({
      primaryCategory: initialValue?.primaryCategory
        ? {
            label: initialValue.primaryCategory.name,
            value: initialValue.primaryCategory,
          }
        : null,
      secondaryCategory: initialValue?.secondaryCategory
        ? {
            label: initialValue.secondaryCategory.name,
            value: initialValue.secondaryCategory,
          }
        : null,
    }),
    [initialValue]
  )

  const validationSchema = useMemo(
    () =>
      Yup.object<GlobalCommandOrgCategoriesFormSchema>({
        primaryCategory: Yup.object<
          SelectValue<IGlobalCommandOrgCategoriesFormPrimaryCategory>
        >()
          .nullable()
          .required(intl.formatMessage(messages.primaryCategoryRequired)),
        secondaryCategory: Yup.object<
          SelectValue<IGlobalCommandOrgCategoriesFormSecondaryCategory>
        >()
          .nullable()
          .required(intl.formatMessage(messages.secondaryCategoryRequired)),
      }),
    [intl]
  )

  const handleSubmit = (
    values: GlobalCommandOrgCategoriesFormSchema,
    { setSubmitting }: FormikHelpers<GlobalCommandOrgCategoriesFormSchema>
  ) => {
    onSubmit({
      primaryCategory: values.primaryCategory?.value ?? null,
      secondaryCategory: values.secondaryCategory?.value ?? null,
    })
    setSubmitting(false)
  }

  return { initialValues, validationSchema, handleSubmit }
}
